import { useEffect, useRef, useState, type ReactNode } from 'react'
import {
  ChevronRight, Pin, PinOff, Star, UserCircle, KeyRound, LogOut,
  Palette, MonitorSmartphone, CalendarDays, type LucideIcon,
} from 'lucide-react'
import { Mark } from '../Logo.js'
import { Link, navigate, useNavigationCount, usePath } from '../router.js'
import { usePrefsStore, setUserPref } from '../prefs.js'
import { apiMutate } from '../admin/primitives.js'
import { useOverlay } from './overlays.js'
import { inputModality } from './modality.js'
import { Floating } from './floating.js'
import { runLogout } from './auth-motion.js'
import { guardedClose, isEditorDirty } from './editing.js'
import { ConfirmDialog } from '../auth/Dialog.js'
import { SectionFavoris } from './SectionFavoris.js'
import { useFavoris } from './favoris.js'
import type { ShellMeta, MeInfo } from './AppShell.js'
import { SITE_TREE, familyForPath, activeChainFor, type NavEntry, type NavFamily } from '../navigation/site-navigation.js'
import { useAdresseCourante } from '../navigation/use-adresse-courante.js'
import { borner, entreesDuModule, familleMontree, menuEpingle, modulesVisibles } from './menu-modele.js'

/**
 * LE MENU FLOTTANT (03/11/2026) — validé par Hugo sur maquette animée,
 * après un premier menu en deux colonnes jugé trop sage.
 *
 *   RAIL       — Favoris, Accueil et les 7 familles en icônes. La souris
 *                entre : il s'élargit et montre leurs noms. La famille de la
 *                page affichée est mise en valeur (dégradé, lueur, trait).
 *   PANNEAU    — fenêtre flottante des modules de la famille survolée, EN
 *                FACE d'elle. Une seule famille à la fois : la liste ne
 *                s'allonge jamais sous la souris.
 *   SOUS-MENUS — fenêtre flottante en face du module survolé. Elle reste
 *                ouverte tant qu'on ne survole pas un autre module qui en a ;
 *                changer de famille la ferme.
 *
 * Délais d'intention : une famille s'ouvre après 140 ms (220 ms si le
 * panneau est déjà ouvert : une diagonale vers lui ne change pas de
 * famille), un module après 110 ms. Tout se referme 380 ms après la sortie
 * de la souris, ou à la navigation. L'épingle (préférence `sidebar` =
 * « pinned ») garde rail et panneau ouverts, dans le flux.
 * Clavier : Tab élargit le menu, Entrée ouvre une famille, Échap referme.
 * Petit écran / tactile : rail et panneau dans un tiroir, sans survol.
 */

/** Recul vertical d'une fenêtre pour que sa PREMIÈRE ligne soit en face de l'élément survolé.
 *  Les hauteurs sont estimées ; la hauteur maximale (100 % moins le décalage) garantit
 *  qu'une fenêtre ne sort jamais de l'écran : sa liste défile au besoin. */
const RECUL_PANNEAU = 56
const LIGNE_PANNEAU = 44
const RECUL_VOLET = 60
const LIGNE_VOLET = 41

const DELAI_FAMILLE = 140
const DELAI_FAMILLE_OUVERT = 220
const DELAI_MODULE = 110
const DELAI_FERMETURE = 380
/** Le rail s'élargit après un souffle : effleurer le bord gauche ne l'ouvre pas. */
const DELAI_ELARGIR = 90

const tousLesModules: NavEntry[] = SITE_TREE.flatMap((f) => f.children)

export function Sidebar(props: {
  meta: ShellMeta
  me: MeInfo | null
  mobileOpen: boolean
  onMobileClose: () => void
}) {
  const path = usePath()
  const navigations = useNavigationCount()
  const prefs = usePrefsStore()
  const epingle = menuEpingle(prefs.resolved['sidebar'])
  const [profileOpen, setProfileOpen] = useState(false)

  /* L'élément actif se lit sur l'ADRESSE, paramètres compris : une fonction
     peut désigner une vue d'une page (`?vue=…`). */
  const adresse = useAdresseCourante(path)
  const activeFamily = familyForPath(adresse)
  const chaineActive = activeChainFor(adresse)

  /* L'état du menu flottant. Les minuteries lisent des refs : un délai lancé
     avant un rendu ne doit pas agir sur un état périmé. */
  const [large, setLarge] = useState(false)
  const [ouvert, setOuvert] = useState(false)
  const [vue, setVueEtat] = useState<string>(activeFamily.id)
  const [modVu, setModVuEtat] = useState<string | null>(null)
  const [hautPanneau, setHautPanneau] = useState(0)
  const [hautVolet, setHautVolet] = useState(0)
  const vueRef = useRef(vue)
  const modVuRef = useRef(modVu)
  const familleIci = useRef(activeFamily.id)
  familleIci.current = activeFamily.id
  const profilRef = useRef(profileOpen)
  profilRef.current = profileOpen
  const epingleRef = useRef(epingle)
  epingleRef.current = epingle
  const setVue = (v: string) => { vueRef.current = v; setVueEtat(v) }
  const setModVu = (m: string | null) => { modVuRef.current = m; setModVuEtat(m) }

  const zoneRef = useRef<HTMLDivElement>(null)
  const panneauRef = useRef<HTMLElement>(null)
  const voletRef = useRef<HTMLElement>(null)
  const tiroirRef = useRef<HTMLDivElement>(null)
  const tFamille = useRef<ReturnType<typeof setTimeout>>(undefined)
  const tModule = useRef<ReturnType<typeof setTimeout>>(undefined)
  const tFermer = useRef<ReturnType<typeof setTimeout>>(undefined)
  const tElargir = useRef<ReturnType<typeof setTimeout>>(undefined)
  const minuteries = () => { clearTimeout(tFamille.current); clearTimeout(tModule.current); clearTimeout(tFermer.current); clearTimeout(tElargir.current) }
  useEffect(() => minuteries, [])

  const panneauVisible = epingle || ouvert
  const railLarge = epingle || large

  /** Tout replier. Épinglé, rail et panneau restent affichés (epingle l'emporte)
   *  et le panneau revient en haut, sur la famille de la page. */
  const replierTout = () => {
    minuteries()
    setModVu(null)
    setVue(familleIci.current)
    setLarge(false)
    setOuvert(false)
    // Flottant, la fenêtre garde sa place pendant qu'elle s'efface.
    if (epingleRef.current) setHautPanneau(0)
  }

  // Chaque navigation referme le menu, le popover profil et le tiroir mobile
  // (une entrée peut désigner une vue `?vue=…` de la page affichée : le
  // compteur change, pas forcément le chemin).
  useEffect(() => {
    setProfileOpen(false)
    props.onMobileClose()
    replierTout()
  }, [navigations]) // eslint-disable-line react-hooks/exhaustive-deps

  const { liste: favoris } = useFavoris()
  const aDesFavoris = favoris.length > 0
  // Plus aucun favori : la vue « Favoris » n'a plus ni bouton ni contenu.
  useEffect(() => { if (!aDesFavoris && vueRef.current === 'favoris') setVue(familleIci.current) }, [aDesFavoris]) // eslint-disable-line react-hooks/exhaustive-deps

  const alertsBadge = props.meta.alertsBadge
  const permissions = props.me?.permissions ?? null
  const peut = (perm?: string) => !perm || permissions === null || permissions.includes(perm)

  // LOT 2.6 — doctrine badges : un badge de navigation = une ACTION MÉTIER
  // canonique à traiter. Les compteurs provider (rapprochements Skello↔UniFi,
  // alertes connecteur) n'en produisent JAMAIS ; le futur Action Center
  // (contrats/congés/heures à valider) rebranchera ces badges proprement.
  const badgeDeFamille = (id: string): number | undefined =>
    id === 'accueil' ? (alertsBadge || undefined) : undefined
  const badgeDEntree = (id: string): number | undefined =>
    id === 'activite' ? (alertsBadge || undefined)
      : id === 'temps' ? (props.meta.tempsBadge || undefined)
        : id === 'associations' ? (props.meta.associationsBadge || undefined)
          : undefined

  const familles = SITE_TREE.filter((f) => familleMontree(f, peut))
  const familleDe = (id: string): NavFamily | null => (id === 'favoris' ? null : (familles.find((f) => f.id === id) ?? activeFamily))
  const montree = familleDe(vue)
  const nbLignes = (id: string) => (id === 'favoris' ? favoris.length : modulesVisibles(familleDe(id)!, peut).length)

  /** Position verticale d'une fenêtre en face de `el`, bornée à la zone du menu. */
  const enFace = (el: HTMLElement, recul: number, hauteur: number): number => {
    const z = zoneRef.current?.getBoundingClientRect()
    if (!z) return 0
    return borner(el.getBoundingClientRect().top - z.top - recul, hauteur, z.height)
  }
  const hauteurPanneau = (id: string) => RECUL_PANNEAU + nbLignes(id) * LIGNE_PANNEAU + 16
  const hauteurVolet = (n: number) => RECUL_VOLET + n * LIGNE_VOLET + 14
  const boutonDeFamille = (id: string) =>
    zoneRef.current?.querySelector<HTMLElement>(`.lc-rail [data-famille="${CSS.escape(id)}"]`) ?? null

  const montrer = (id: string, haut: number) => {
    if (id !== vueRef.current) {
      setModVu(null)
      // Le focus clavier dans le panneau suit la famille : sa liste va être remplacée.
      const focus = document.activeElement
      if (panneauRef.current?.contains(focus) || voletRef.current?.contains(focus)) boutonDeFamille(id)?.focus()
    }
    setVue(id)
    setHautPanneau(epingle ? 0 : haut)
    setOuvert(true)
    setLarge(true)
  }
  const viserFamille = (id: string, el: HTMLElement) => {
    clearTimeout(tFamille.current)
    const haut = enFace(el, RECUL_PANNEAU, hauteurPanneau(id))
    tFamille.current = setTimeout(() => montrer(id, haut), panneauVisible ? DELAI_FAMILLE_OUVERT : DELAI_FAMILLE)
  }
  /** Ouvre les sous-menus de `m` en face de `el`. Un module SANS sous-menus
   *  ne referme pas la fenêtre : seul un autre module qui en a la remplace. */
  const viserModule = (m: NavEntry, el: HTMLElement, immediat = false) => {
    clearTimeout(tModule.current)
    const n = entreesDuModule(m, adresse, peut).length
    if (n === 0 || m.id === modVuRef.current) return
    const haut = enFace(el, RECUL_VOLET, hauteurVolet(n))
    const ouvrir = () => { setModVu(m.id); setHautVolet(haut) }
    if (immediat) ouvrir()
    else tModule.current = setTimeout(ouvrir, DELAI_MODULE)
  }

  /* ENTRÉE ET SORTIE DE LA SOURIS : écouteurs NATIFS sur la zone. Ceux de
     React suivent l'arbre des composants, popovers et dialogues en portail
     compris : survoler la confirmation de déconnexion « entrait » dans le
     menu, et un portail démonté sous la souris le laissait figé ouvert. Le
     DOM, lui, ne connaît que le rail et les fenêtres. */
  const fermerPlusTard = () => {
    clearTimeout(tFermer.current)
    tFermer.current = setTimeout(function fermer() {
      // Le popover profil, une fenêtre du pied (état, profil) survolée, ou le focus clavier dans
      // le menu : on réessaie plus tard au lieu de replier sous l'utilisateur.
      const zone = zoneRef.current
      if (profilRef.current || document.querySelector('.lc-pop-wrap:hover') || (inputModality() === 'keyboard' && zone?.contains(document.activeElement))) {
        tFermer.current = setTimeout(fermer, DELAI_FERMETURE)
        return
      }
      replierTout()
    }, DELAI_FERMETURE)
  }
  useEffect(() => {
    const zone = zoneRef.current
    if (!zone) return
    const entrer = () => {
      clearTimeout(tFermer.current)
      clearTimeout(tElargir.current)
      tElargir.current = setTimeout(() => setLarge(true), DELAI_ELARGIR)
    }
    const sortir = () => {
      clearTimeout(tElargir.current)
      clearTimeout(tFamille.current)
      clearTimeout(tModule.current)
      fermerPlusTard()
    }
    zone.addEventListener('mouseenter', entrer)
    zone.addEventListener('mouseleave', sortir)
    return () => { zone.removeEventListener('mouseenter', entrer); zone.removeEventListener('mouseleave', sortir) }
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  // Après un changement d'épingle, le menu repart replié (détaché, il reste
  // ouvert sous la souris : voir basculerEpingle).
  const detache = useRef(false)
  useEffect(() => {
    if (detache.current) { detache.current = false; return }
    setLarge(false); setOuvert(false); setModVu(null); setHautPanneau(0)
  }, [epingle]) // eslint-disable-line react-hooks/exhaustive-deps

  /** Les événements d'un portail (popover, dialogue) remontent ici par React : on les ignore. */
  const dansLaZone = (e: React.SyntheticEvent) => !!zoneRef.current?.contains(e.target as Node)
  const focusEntre = (e: React.FocusEvent) => {
    if (dansLaZone(e) && inputModality() === 'keyboard') { clearTimeout(tFermer.current); setLarge(true) }
  }
  const focusSort = (e: React.FocusEvent) => {
    // La souris est encore dessus : sa sortie prendra le relais.
    if (zoneRef.current?.matches(':hover')) return
    if (!zoneRef.current?.contains(e.relatedTarget as Node)) fermerPlusTard()
  }
  /** Échap referme et rend le focus au bouton de la famille ; jamais au <body>. */
  const touche = (e: React.KeyboardEvent) => {
    // Un popover du pied ouvert (état, profil) : Échap ne ferme que lui (pile des calques).
    if (e.key !== 'Escape' || !dansLaZone(e) || profilRef.current || document.querySelector('.lc-pop-wrap')) return
    const actif = document.activeElement as HTMLElement | null
    const id = vueRef.current
    replierTout()
    if (actif?.closest('.lc-panneau, .lc-volet')) boutonDeFamille(id)?.focus()
  }
  /** Cliquer l'entrée de la page DÉJÀ affichée n'est pas une navigation (le routeur l'ignore) : on replie quand même. */
  const clicZone = (e: React.MouseEvent) => {
    if (e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return
    if ((e.target as HTMLElement).closest?.('a')?.getAttribute('href') === adresse) replierTout()
  }
  const basculerEpingle = () => {
    if (epingle) {
      // Détacher : le menu reste ouvert sous la souris, puis se referme
      // normalement après la sortie.
      detache.current = true
      clearTimeout(tFermer.current)
      setOuvert(true)
      setLarge(true)
    }
    void setUserPref({ sidebar: epingle ? 'auto' : 'pinned' }, apiMutate)
  }
  /** Au clavier, ouvrir une famille envoie le focus dans son panneau. */
  const focusDansPanneau = () => requestAnimationFrame(() => {
    const p = panneauRef.current
    p?.querySelector<HTMLElement>('.lc-panneau__liste a, .lc-favs a')?.focus()
  })

  const boutonRail = (tiroir: boolean, id: string, Icone: LucideIcon, nom: string, badge?: number) => {
    const vu = (tiroir || panneauVisible) && id === vue
    return (
      <button
        key={id}
        type="button"
        data-famille={tiroir ? undefined : id}
        className={`lc-rail__btn${id === activeFamily.id ? ' lc-rail__btn--ici' : ''}${vu && id !== activeFamily.id ? ' lc-rail__btn--vu' : ''}`}
        aria-current={id === activeFamily.id ? 'true' : undefined}
        {...(tiroir ? { 'aria-pressed': vu } : { 'aria-expanded': vu, 'aria-controls': 'lc-menu-panneau' })}
        aria-label={badge ? `${nom} (${badge})` : nom}
        onMouseEnter={tiroir ? undefined : (e) => viserFamille(id, e.currentTarget)}
        onMouseLeave={tiroir ? undefined : () => clearTimeout(tFamille.current)}
        onClick={(e) => {
          clearTimeout(tFamille.current)
          if (tiroir) { setVue(id); return }
          montrer(id, enFace(e.currentTarget, RECUL_PANNEAU, hauteurPanneau(id)))
          if (e.detail === 0 || inputModality() === 'keyboard') focusDansPanneau()
        }}
      >
        <Icone size={21} strokeWidth={1.9} aria-hidden="true" className="lc-rail__icone" />
        <span className="lc-rail__nom">{nom}</span>
        {badge ? <span className="lc-rail__pastille" aria-hidden="true">{badge > 9 ? '9+' : badge}</span> : null}
      </button>
    )
  }

  const rail = (tiroir: boolean) => (
    <div className={`lc-rail${railLarge || tiroir ? ' lc-rail--large' : ''}`}>
      {/* Le logo RAMÈNE À L'ACCUEIL (§47) — un vrai lien. */}
      <Link href="/" className="lc-brand__home lc-rail__marque" aria-label="Accueil Gestion">
        <span className="lc-brand__mark">
          {prefs.branding.assets['logo_compact']
            ? <img className="lc-brand__img" src={prefs.branding.assets['logo_compact']!.url} alt="" />
            : <Mark size={22} />}
        </span>
        <span className="lc-rail__nom lc-rail__marquetexte">
          <strong>{prefs.branding.app_name.toUpperCase()}</strong>
          <span>{prefs.branding.org_name}</span>
        </span>
      </Link>
      <nav className="lc-rail__familles" aria-label="Familles">
        {aDesFavoris ? boutonRail(tiroir, 'favoris', Star, 'Favoris') : null}
        {familles.map((f) => boutonRail(tiroir, f.id, f.icon, f.label, badgeDeFamille(f.id)))}
      </nav>
      <div className="lc-rail__pied">
        <ProfileBlock me={props.me} open={profileOpen} onToggle={() => setProfileOpen(!profileOpen)} onClose={() => setProfileOpen(false)} />
      </div>
    </div>
  )

  const panneau = (tiroir: boolean) => {
    const visible = tiroir || panneauVisible
    return (
      <aside
        ref={tiroir ? undefined : panneauRef}
        id={tiroir ? undefined : 'lc-menu-panneau'}
        className={`lc-side2 lc-flot lc-panneau${visible ? ' lc-flot--ouvert' : ''}`}
        style={tiroir ? undefined : { marginTop: hautPanneau, maxHeight: `calc(100% - ${hautPanneau}px)` }}
        aria-label={montree ? montree.label : 'Favoris'}
        inert={!visible}
      >
        <div className="lc-panneau__tete">
          {montree
            ? <Link href={montree.route} className="lc-panneau__titre">{montree.label}</Link>
            : <span className="lc-panneau__titre">Favoris</span>}
          {tiroir ? null : (
            <button type="button" className={`lc-panneau__epingle${epingle ? ' lc-panneau__epingle--on' : ''}`} onClick={basculerEpingle}
              aria-pressed={epingle} aria-label={epingle ? 'Détacher le menu : il se refermera tout seul' : 'Épingler le menu ouvert'}
              title={epingle ? 'Détacher le menu' : 'Épingler le menu'}>
              {epingle ? <PinOff size={15} aria-hidden="true" /> : <Pin size={15} aria-hidden="true" />}
            </button>
          )}
        </div>
        {montree === null ? <SectionFavoris adresse={adresse} peut={peut} sansTete /> : (
          /* key = remontage à chaque famille : les lignes rejouent leur entrée. */
          <nav className="lc-nav lc-panneau__liste" key={montree.id} aria-label={`Modules ${montree.label}`}>
            {modulesVisibles(montree, peut).map((c, i) => {
              const actif = montree.id === activeFamily.id && chaineActive[0]?.id === c.id
              const sous = entreesDuModule(c, adresse, peut)
              const aDesSous = sous.length > 0 && !tiroir
              const badge = badgeDEntree(c.id)
              return (
                <Link
                  key={c.id}
                  href={c.route!}
                  className={`lc-nav__item lc-panneau__mod${c.state === 'NOT_CONFIGURED' ? ' lc-panneau__mod--off' : ''}${modVu === c.id ? ' lc-panneau__mod--deplie' : ''}`}
                  style={{ animationDelay: `${i * 28}ms` }}
                  aria-current={actif ? (chaineActive.length > 1 ? 'true' : 'page') : undefined}
                  {...(aDesSous ? { 'aria-expanded': modVu === c.id, 'aria-controls': 'lc-menu-volet' } : {})}
                  onMouseEnter={tiroir ? undefined : (e) => viserModule(c, e.currentTarget)}
                  onFocus={tiroir ? undefined : (e) => { if (inputModality() === 'keyboard') viserModule(c, e.currentTarget, true) }}
                  onKeyDown={aDesSous ? (e) => {
                    // → : entrer dans les sous-menus de CE module.
                    if (e.key !== 'ArrowRight') return
                    e.preventDefault()
                    if (modVuRef.current !== c.id) { setModVu(c.id); setHautVolet(enFace(e.currentTarget, RECUL_VOLET, hauteurVolet(sous.length))) }
                    requestAnimationFrame(() => voletRef.current?.querySelector<HTMLElement>('.lc-volet__entree')?.focus())
                  } : undefined}
                >
                  <span className="lc-panneau__puce" aria-hidden="true" />
                  <span className="lc-nav__label">{c.label}</span>
                  {badge ? <span className="lc-nav__badge">{badge}</span> : null}
                  {aDesSous ? <ChevronRight size={14} className="lc-panneau__chev" aria-hidden="true" /> : null}
                </Link>
              )
            })}
          </nav>
        )}
      </aside>
    )
  }

  const modOuvert = panneauVisible && modVu ? tousLesModules.find((m) => m.id === modVu) ?? null : null
  const entreesVolet = modOuvert ? entreesDuModule(modOuvert, adresse, peut) : []
  const volet = (
    <aside
      ref={voletRef}
      id="lc-menu-volet"
      className={`lc-flot lc-volet${modOuvert ? ' lc-flot--ouvert' : ''}`}
      style={{ marginTop: hautVolet, maxHeight: `calc(100% - ${hautVolet}px)` }}
      aria-label={modOuvert ? `Sous-menus ${modOuvert.label}` : undefined}
      inert={!modOuvert}
      onKeyDown={(e) => {
        // ← : revenir au module dans le panneau.
        if (e.key !== 'ArrowLeft' || !modOuvert) return
        e.preventDefault()
        panneauRef.current?.querySelector<HTMLElement>(`.lc-panneau__mod[aria-controls="lc-menu-volet"][aria-expanded="true"]`)?.focus()
      }}
    >
      {modOuvert ? (
        <>
          <div className="lc-volet__tete">
            <span className="lc-volet__titre">{modOuvert.label}</span>
            <span className="lc-volet__famille">{familleDe(vue)?.label ?? ''}</span>
          </div>
          <nav className="lc-volet__liste" key={modOuvert.id}>
            {entreesVolet.map((x, i) => (
              <Link key={x.id} href={x.route} className="lc-volet__entree" style={{ animationDelay: `${i * 26}ms` }}
                aria-current={x.actif ? 'page' : undefined}>
                <span>{x.label}</span>
              </Link>
            ))}
          </nav>
        </>
      ) : null}
    </aside>
  )

  /* Tiroir mobile : Échap le ferme (pile commune des calques), le défilement
     de la page est bloqué, le focus y entre et revient au bouton menu. */
  useOverlay(props.mobileOpen, props.onMobileClose, { lockScroll: true })
  useEffect(() => {
    if (!props.mobileOpen) return
    const avant = document.activeElement as HTMLElement | null
    const t = tiroirRef.current
    // Par ordre de préférence (une liste à virgules prendrait le premier du DOM : le logo).
    const cible = ['.lc-panneau [aria-current]', '.lc-rail__btn[aria-pressed="true"]', 'button, a']
      .map((sel) => t?.querySelector<HTMLElement>(sel)).find(Boolean) ?? t
    cible?.focus()
    return () => { avant?.focus?.() }
  }, [props.mobileOpen])

  return (
    <>
      {/* La place réservée dans la grille : le rail replié, ou le menu épinglé.
          Le menu flottant se superpose au contenu, qui ne bouge pas. */}
      <div className={`lc-siderail${epingle ? ' lc-siderail--pinned' : ''}`} aria-hidden={props.mobileOpen ? 'true' : undefined}>
        <div className={`lc-menu-voile${(panneauVisible || large) && !epingle ? ' lc-menu-voile--on' : ''}`} aria-hidden="true" />
        <div
          ref={zoneRef}
          className={`lc-menunav${railLarge ? ' lc-menunav--large' : ''}`}
          onFocus={focusEntre}
          onBlur={focusSort}
          onKeyDown={touche}
          onClick={clicZone}
        >
          {rail(false)}
          {panneau(false)}
          {volet}
        </div>
      </div>

      {/* Petit écran / tactile : rail et panneau dans un tiroir. Toucher
          l'entrée de l'écran DÉJÀ affiché (adresse identique) n'est pas une
          navigation — le routeur l'ignore : le tiroir se referme quand même. */}
      {props.mobileOpen ? (
        <div className="lc-sidedrawer-backdrop" onClick={props.onMobileClose} role="presentation">
          <div
            ref={tiroirRef}
            id="lc-nav-tiroir"
            tabIndex={-1}
            className="lc-sidedrawer lc-sidedrawer--menu"
            onClick={(e) => {
              e.stopPropagation()
              if ((e.target as HTMLElement).closest?.('a')?.getAttribute('href') === adresse) props.onMobileClose()
            }}
            aria-label="Navigation"
            aria-modal="true"
            role="dialog"
          >
            {rail(true)}
            {panneau(true)}
          </div>
        </div>
      ) : null}
    </>
  )
}

/* ═══════════════════ PROFIL (bas de sidebar) ═══════════════════ */

export function initialsOf(name: string): string {
  return name.split(/\s+/).map((p) => p[0]).join('').slice(0, 2).toUpperCase() || '?'
}

/** Avatar : image du compte si présente, sinon initiales. */
export function AvatarBubble(props: { me: MeInfo; className?: string }) {
  return (
    <span className={`lc-avatar ${props.className ?? ''}`}>
      {props.me.avatar
        ? <img className="lc-avatar__img" src={props.me.avatar} alt="" />
        : initialsOf(props.me.display_name)}
      <span className="lc-profile__presence" aria-hidden="true" />
    </span>
  )
}

function ProfileBlock(props: { me: MeInfo | null; open: boolean; onToggle: () => void; onClose: () => void }) {
  useOverlay(props.open, props.onClose)
  const me = props.me
  const btnRef = useRef<HTMLButtonElement>(null)
  const [confirmLogout, setConfirmLogout] = useState(false)

  // Déconnexion = action atomique : confirmation directe (jamais la Save
  // Bar). Si une édition est en cours, la garde « Modifications non
  // enregistrées » passe D'ABORD — rien ne se perd en silence.
  const demanderLogout = () => {
    props.onClose()
    guardedClose(isEditorDirty(), () => setConfirmLogout(true))
  }

  const entry = (href: string, Icon: LucideIcon, label: string): ReactNode => (
    <button className="lc-pop__item" onClick={() => { props.onClose(); navigate(href) }}>
      <Icon size={16} /> {label}
    </button>
  )

  return (
    <div className="lc-profile">
      <button
        ref={btnRef}
        className="lc-profile__btn"
        onClick={props.onToggle}
        aria-haspopup="menu"
        aria-expanded={props.open}
        data-tip={me ? me.display_name : 'Compte'}
      >
        {me ? <AvatarBubble me={me} className="lc-profile__avatar" /> : (
          <span className="lc-avatar lc-profile__avatar lc-profile__avatar--anon">
            <UserCircle size={19} />
            <span className="lc-profile__presence" aria-hidden="true" />
          </span>
        )}
        <span className="lc-profile__text">
          <span className="lc-profile__name">{me ? me.display_name : 'Non connecté'}</span>
          <span className="lc-profile__role">{me ? me.role_label : 'Session anonyme — développement'}</span>
        </span>
      </button>

      {/* Portal + collision : le popover ne peut ni être découpé par la
          sidebar ni sortir du viewport. */}
      <Floating open={props.open} anchorRef={btnRef} placement="top-start" className="lc-pop-wrap" onOutside={props.onClose}>
        <div className="lc-pop" role="menu" aria-label="Compte">
          {me ? (
            <>
              <div className="lc-pop__head">
                <AvatarBubble me={me} className="lc-profile__avatar" />
                <span>
                  <div className="lc-pop__name">{me.display_name}</div>
                  <div className="lc-pop__mail">{me.email}</div>
                </span>
              </div>
              {entry('/moi', CalendarDays, 'Mon espace — temps de travail')}
              {entry('/profile', UserCircle, 'Mon profil')}
              {entry('/profile#security', KeyRound, 'Sécurité & MFA')}
              {entry('/profile#sessions', MonitorSmartphone, 'Mes sessions')}
              {entry('/settings/appearance', Palette, 'Préférences')}
              <div className="lc-pop__rule" />
              <button className="lc-pop__item lc-pop__item--danger" onClick={demanderLogout}>
                <LogOut size={16} /> Se déconnecter
              </button>
            </>
          ) : (
            <>
              <div className="lc-pop__head">
                <span className="lc-avatar lc-profile__avatar lc-profile__avatar--anon"><UserCircle size={19} /></span>
                <span>
                  <div className="lc-pop__name">Aucune session</div>
                  <div className="lc-pop__mail">L’authentification n’est pas encore imposée.</div>
                </span>
              </div>
              <button className="lc-pop__item" onClick={() => window.location.assign('/login')}>
                <KeyRound size={16} /> Se connecter
              </button>
            </>
          )}
        </div>
      </Floating>

      <ConfirmDialog
        open={confirmLogout}
        danger={false}
        title="Se déconnecter ?"
        confirmLabel="Se déconnecter"
        onConfirm={() => {
          setConfirmLogout(false)
          // Ordre de sécurité : la session est INVALIDÉE CÔTÉ SERVEUR avant
          // toute animation (runLogout attend la réponse du POST logout).
          void runLogout()
        }}
        onClose={() => setConfirmLogout(false)}
      >
        Vous devrez vous authentifier à nouveau pour accéder à Gestion.
      </ConfirmDialog>
    </div>
  )
}
